import { BroadcastChannel } from 'broadcast-channel'

import {
    CHANGE_RULES,
    CHANGE_SAVE_STATE,
    CLOSE_SIDEVIEW,
    LOAD_NEW_RULE,
    LOAD_RIG_STATE_OUTPUTS,
    LOAD_RULES,
    NEW_RULE,
    OPEN_SIDEVIEW,
    REMOVE_RULES,
    RESTORE_RULE,
    SECOND_HIGHEST_SEVERITY,
    SET_GLOBAL_ALERT,
    SET_LOADING,
    TOGGLE_SELECTED,
    UPDATE_ASSET_ID,
    UPDATE_BUTTON_SAVE_STATUS,
    UPDATE_CURVES,
    UPDATE_RULES_SERVICE,
    UPDATE_SOUND_STATUS
} from '../constants'

const editorInitialState = () => ({
    assetId: null,
    assetRules: { user: [], global: [] },
    broadcastChannel: new BroadcastChannel('plugin-channel-alarms'),
    buttonSaveStatus: { saving: false, button: '' },
    canEditGlobal: false,
    channel: null,
    curves: null,
    globalAlert: { message: '', success: true },
    isOpen: false,
    loading: true,
    localRules: [],
    rigStateOutputs: [],
    savedRules: {},
    saveState: [],
    selected: new Set(),
    soundStatus: {}
})

export default function editorReducer(state = editorInitialState(), action) {
    if (!action?.type) return state

    switch (action.type) {
        case CHANGE_RULES: {
            if (!action.changes || !Array.isArray(action.changes)) return state

            let { localRules, savedRules, saveState, soundStatus } = state

            action.changes.forEach(change => {
                localRules = [
                    ...localRules.slice(0, change.index),
                    {
                        ...localRules[change.index],
                        ...change.keyVals,
                        ...(action.saved ? { new: undefined } : {})
                    },
                    ...localRules.slice(change.index + 1)
                ]

                if (action.update) {
                    savedRules = {
                        ...savedRules,
                        [change.index]: {
                            ...state.savedRules[change.index],
                            ...change.keyVals,
                            ...(action.saved ? { new: undefined } : {})
                        }
                    }

                    if (action.saved) {
                        saveState = [
                            ...saveState.slice(0, change.index),
                            'success',
                            ...saveState.slice(change.index + 1)
                        ]
                    }

                    if (change.keyVals?.sound !== undefined) {
                        const id = state.savedRules[change.index]?.id || change.keyVals?.id

                        if (id) {
                            soundStatus = {
                                ...soundStatus,
                                [id]: change.keyVals.sound
                            }
                        }
                    }
                } else {
                    saveState = saveState = [
                        ...saveState.slice(0, change.index),
                        change.saveState,
                        ...saveState.slice(change.index + 1)
                    ]
                }
            })

            return {
                ...state,
                localRules,
                savedRules,
                saveState,
                soundStatus
            }
        }
        case CHANGE_SAVE_STATE:
            return {
                ...state,
                saveState: [
                    ...state.saveState.slice(0, action.index),
                    action.saveState,
                    ...state.saveState.slice(action.index + 1)
                ]
            }
        case CLOSE_SIDEVIEW:
            return {
                ...state,
                isOpen: false
            }
        case LOAD_NEW_RULE:
            return {
                ...state,
                localRules: [...state.localRules, { ...action.rule }],
                savedRules: {
                    ...state.savedRules,
                    [action.rule.index]: {
                        ...action.rule
                    }
                },
                saveState: [...state.saveState, '']
            }
        case LOAD_RIG_STATE_OUTPUTS:
            return {
                ...state,
                rigStateOutputs: action.outputs
            }
        case LOAD_RULES: {
            const rules = [
                ...action.userRules.map(rule => ({ ...rule, global: false })),
                ...action.globalRules.map(rule => ({ ...rule, global: true }))
            ].map((r, index) => ({ ...r, index }))

            const savedRules = rules.reduce((obj, rule, idx) => ({ ...obj, [idx]: rule }), {})

            return {
                ...state,
                canEditGlobal: action.canEditGlobal,
                loading: false,
                localRules: rules,
                savedRules,
                saveState: rules.map(() => '')
            }
        }
        case NEW_RULE:
            return {
                ...state,
                localRules: [
                    ...state.localRules,
                    {
                        sound: true,
                        severity: SECOND_HIGHEST_SEVERITY,
                        description: '',
                        active: true,
                        ...action.rule,
                        id: action.newRuleId,
                        index: state.localRules.length,
                        new: true
                    }
                ],
                saveState: [...state.saveState, 'success']
            }
        case OPEN_SIDEVIEW:
            return {
                ...state,
                isOpen: true,
                channel: action.channel
            }
        case REMOVE_RULES: {
            const savedRules = {}
            const currentIndices = Object.keys(state.savedRules)
                .map(k => parseInt(k))
                .sort()
            let counter = 0
            currentIndices.forEach(idx => {
                if (!action.indices.includes(idx)) {
                    savedRules[counter] = { ...state.savedRules[idx], index: counter }
                    counter++
                }
            })

            return {
                ...state,
                localRules: state.localRules
                    .filter((_, idx) => !action.indices.includes(idx))
                    .map((rule, idx) => ({ ...rule, index: idx })),
                saveState: state.saveState.filter((_, idx) => !action.indices.includes(idx)),
                savedRules
            }
        }
        case RESTORE_RULE:
            return {
                ...state,
                localRules: [
                    ...state.localRules.slice(0, action.index),
                    state.savedRules[action.index],
                    ...state.localRules.slice(action.index + 1)
                ],
                saveState: [...state.saveState.slice(0, action.index), '', ...state.saveState.slice(action.index + 1)]
            }
        case SET_GLOBAL_ALERT:
            return {
                ...state,
                globalAlert: {
                    message: action.message,
                    success: action.success
                }
            }
        case SET_LOADING:
            return {
                ...state,
                loading: action.loading
            }
        case TOGGLE_SELECTED:
            return {
                ...state,
                selected: new Set(
                    action.sameValue
                        ? action.indices.every(idx => state.selected.has(idx))
                            ? [...state.selected].filter(idx => !action.indices.includes(idx))
                            : [...state.selected, ...action.indices]
                        : [
                              ...[...state.selected].filter(idx => !action.indices.includes(idx)),
                              ...action.indices.filter(idx => !state.selected.has(idx))
                          ]
                )
            }
        case UPDATE_ASSET_ID:
            return {
                ...state,
                assetId: action.id,
                localRules: [],
                savedRules: {}
            }
        case UPDATE_BUTTON_SAVE_STATUS:
            return {
                ...state,
                buttonSaveStatus: action.status
            }
        case UPDATE_CURVES:
            return {
                ...state,
                curves: {
                    ...action.curves
                }
            }
        case UPDATE_RULES_SERVICE:
            return {
                ...state,
                assetRules: {
                    user: action.rules.userRules,
                    global: action.rules.allUsersRules
                }
            }
        case UPDATE_SOUND_STATUS:
            return {
                ...state,
                loading: false,
                soundStatus: {
                    ...state.soundStatus,
                    ...action.soundStatus
                }
            }
        default:
            return state
    }
}
